// GeoPin game rules shared by the browser game and the multiplayer Worker.
// Keep this file free of DOM and Worker APIs so both sides import the same logic.

export type PlaceType = "cap" | "rcap" | "city" | "mark";

/** A playable place, as stored in catalog/places.json (compact keys). */
export interface Place {
  id: string;
  n: string;        // name
  t: PlaceType;
  c: string;        // ISO country code
  r?: string;       // region (state / province) code
  lat: number;
  lng: number;
  p: number;        // population, 0 if unknown
  d: 1 | 2 | 3 | 4; // difficulty
}

export interface Region { code: string; name: string; bbox: BBox; count: number }
export interface Country {
  code: string; name: string; continent: string; bbox: BBox;
  counts: Partial<Record<PlaceType, number>>; regionWord: string; regions: Region[];
}
export interface Continent { name: string; bbox: BBox; capitals: number }
export interface CatalogIndex { version: number; countries: Country[]; continents: Continent[] }

/** [west, south, east, north]. East may exceed 180 for boxes that cross the antimeridian. */
export type BBox = [number, number, number, number];

export type Scope =
  | { kind: "world" }
  | { kind: "continent"; name: string }
  | { kind: "country"; code: string; regions?: string[] };

export type Difficulty = "easy" | "medium" | "hard" | "expert";

export interface GameConfig {
  scope: Scope;
  kinds: PlaceType[];
  difficulty: Difficulty;
  rounds: number;
  timer: number;            // seconds per round; 0 = no limit (solo only)
  mode: "standard" | "blitz";
}

export const DEFAULT_CONFIG: GameConfig = {
  scope: { kind: "country", code: "US" },
  kinds: ["cap", "rcap", "city"],
  difficulty: "medium",
  rounds: 10,
  timer: 20,
  mode: "standard",
};

export const MAX_POINTS = 1000;
export const SPEED_BONUS = 50;
const DIFF_LEVEL: Record<Difficulty, number> = { easy: 1, medium: 2, hard: 3, expert: 4 };
const KINDS: PlaceType[] = ["cap", "rcap", "city", "mark"];

// ---------------------------------------------------------------- geometry

export function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371.0088;
  const toRad = Math.PI / 180;
  const dLat = (lat2 - lat1) * toRad;
  const dLng = (lng2 - lng1) * toRad;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * toRad) * Math.cos(lat2 * toRad) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
}

export const WORLD_BBOX: BBox = [-170, -56, 190, 72];

/** The bounding box a game is played in. */
export function scopeBBox(scope: Scope, index: CatalogIndex): BBox {
  if (scope.kind === "world") return WORLD_BBOX;
  if (scope.kind === "continent") return index.continents.find(c => c.name === scope.name)?.bbox ?? WORLD_BBOX;
  const country = index.countries.find(c => c.code === scope.code);
  if (!country) return WORLD_BBOX;
  if (scope.regions && scope.regions.length) {
    const boxes = country.regions.filter(r => scope.regions!.includes(r.code)).map(r => r.bbox);
    if (boxes.length) return unionBBox(boxes);
  }
  return country.bbox;
}

export function unionBBox(boxes: BBox[]): BBox {
  return boxes.reduce((u, b) => [Math.min(u[0], b[0]), Math.min(u[1], b[1]), Math.max(u[2], b[2]), Math.max(u[3], b[3])],
    [Infinity, Infinity, -Infinity, -Infinity] as BBox);
}

/** Size of the play area (km across), which sets how forgiving scoring is. */
export function scopeSpanKm(b: BBox): number {
  const w = Math.min(360, b[2] - b[0]);
  const midLat = (b[1] + b[3]) / 2;
  const across = haversineKm(midLat, 0, midLat, Math.min(w, 179.9)) * (w > 179.9 ? w / 179.9 : 1);
  const tall = haversineKm(b[1], 0, b[3], 0);
  return Math.max(1, Math.hypot(across, tall));
}

// ---------------------------------------------------------------- scoring

export interface ScoreScale { perfectKm: number; falloffKm: number }

/** Full points inside `perfectKm`; beyond it points fall off exponentially over `falloffKm`.
 *  Both grow with the map, so a whole-world game is as fair as one state. */
export function scaleFor(spanKm: number): ScoreScale {
  return {
    perfectKm: clamp(spanKm * 0.005, 1, 100),
    falloffKm: clamp(spanKm * 0.05, 8, 1000),
  };
}

export function scoreDistance(km: number, s: ScoreScale): number {
  if (!Number.isFinite(km) || km < 0) return 0;
  if (km <= s.perfectKm) return MAX_POINTS;
  const pts = Math.round(MAX_POINTS * Math.exp(-(km - s.perfectKm) / s.falloffKm));
  return pts < 5 ? 0 : pts;
}

/** Up to SPEED_BONUS extra points for answering early in a timed round (only if the pin scored). */
export function speedBonus(points: number, elapsedMs: number, timerSec: number): number {
  if (!timerSec || points <= 0) return 0;
  const left = 1 - clamp(elapsedMs / (timerSec * 1000), 0, 1);
  return Math.round(SPEED_BONUS * left);
}

/** Ring radii (km) drawn at the reveal: the perfect zone, then where points drop to 750 / 500 / 250. */
export function ringRadii(s: ScoreScale): { km: number; points: number }[] {
  return [MAX_POINTS, 750, 500, 250].map(p => ({
    points: p,
    km: p === MAX_POINTS ? s.perfectKm : s.perfectKm + s.falloffKm * Math.log(MAX_POINTS / p),
  }));
}

// ---------------------------------------------------------------- rounds

export function inScope(p: Place, scope: Scope, countryContinent: Map<string, string>): boolean {
  if (scope.kind === "world") return true;
  if (scope.kind === "continent") return countryContinent.get(p.c) === scope.name;
  if (p.c !== scope.code) return false;
  return !scope.regions || scope.regions.length === 0 || (!!p.r && scope.regions.includes(p.r));
}

/** In world/continent games, "capitals" means national capitals and "cities" means each
 *  country's best-known cities; state capitals and landmarks only make sense inside a country. */
export function effectiveKinds(cfg: GameConfig): PlaceType[] {
  const k = cfg.kinds.filter(x => KINDS.includes(x));
  if (cfg.scope.kind === "country") return k.length ? k : ["cap", "rcap", "city"];
  const wide = k.filter(x => x === "cap" || x === "city");
  return wide.length ? wide : ["cap"];
}

export function eligible(all: Place[], cfg: GameConfig, countryContinent: Map<string, string>): Place[] {
  const kinds = effectiveKinds(cfg);
  const wide = cfg.scope.kind !== "country";
  return all.filter(p => kinds.includes(p.t) && inScope(p, cfg.scope, countryContinent)
    // across many countries, keep "cities" to each country's well-known ones
    && !(wide && p.t === "city" && p.d > 2));
}

/** Picks the rounds. Never returns more rounds than there are places, widens difficulty
 *  when a small map runs short, and spreads picks across countries or regions. */
export function pickRounds(all: Place[], cfg: GameConfig, countryContinent: Map<string, string>,
                           rand: () => number = Math.random): Place[] {
  const pool = eligible(all, cfg, countryContinent);
  let level = DIFF_LEVEL[cfg.difficulty] ?? 2;
  let candidates = pool.filter(p => p.d <= level);
  while (candidates.length < cfg.rounds && level < 4) {
    level++;
    candidates = pool.filter(p => p.d <= level);
  }
  // On Hard/Expert, prefer the harder places but still allow easier ones if needed.
  const minPreferred = cfg.difficulty === "hard" ? 3 : cfg.difficulty === "expert" ? 4 : 1;
  const preferred = shuffle(candidates.filter(p => p.d >= minPreferred), rand);
  const rest = shuffle(candidates.filter(p => p.d < minPreferred), rand);
  const ordered = [...preferred, ...rest];

  const groupOf = (p: Place) => (cfg.scope.kind === "country" ? p.r ?? p.c : p.c);
  const buckets = new Map<string, Place[]>();
  for (const p of ordered) {
    const g = groupOf(p);
    if (!buckets.has(g)) buckets.set(g, []);
    buckets.get(g)!.push(p);
  }
  const groups = shuffle([...buckets.keys()], rand);
  const out: Place[] = [];
  const n = Math.min(cfg.rounds, ordered.length);
  while (out.length < n) {
    let added = false;
    for (const g of groups) {
      const b = buckets.get(g)!;
      if (b.length && out.length < n) { out.push(b.shift()!); added = true; }
    }
    if (!added) break;
  }
  return shuffle(out, rand);
}

// ---------------------------------------------------------------- config validation

/** Cleans an untrusted config (from a URL or a multiplayer message). */
export function sanitizeConfig(raw: any, index?: CatalogIndex): GameConfig {
  const c = raw && typeof raw === "object" ? raw : {};
  let scope: Scope = DEFAULT_CONFIG.scope;
  const s = c.scope;
  if (s && s.kind === "world") scope = { kind: "world" };
  else if (s && s.kind === "continent" && typeof s.name === "string" && (!index || index.continents.some(x => x.name === s.name)))
    scope = { kind: "continent", name: s.name };
  else if (s && s.kind === "country" && typeof s.code === "string" && /^[A-Z]{2}$/.test(s.code)
    && (!index || index.countries.some(x => x.code === s.code))) {
    const regions = Array.isArray(s.regions) ? s.regions.filter((r: unknown) => typeof r === "string" && r.length < 80).slice(0, 100) : undefined;
    scope = { kind: "country", code: s.code, ...(regions && regions.length ? { regions } : {}) };
  }
  const kinds = Array.isArray(c.kinds) ? (c.kinds as unknown[]).filter((k): k is PlaceType => KINDS.includes(k as PlaceType)) : DEFAULT_CONFIG.kinds;
  const difficulty: Difficulty = (["easy", "medium", "hard", "expert"] as const).includes(c.difficulty) ? c.difficulty : DEFAULT_CONFIG.difficulty;
  const rounds = clamp(Math.round(Number(c.rounds) || DEFAULT_CONFIG.rounds), 3, 30);
  const timerRaw = Number(c.timer);
  const timer = timerRaw === 0 ? 0 : clamp(Math.round(timerRaw || DEFAULT_CONFIG.timer), 5, 60);
  const mode = c.mode === "blitz" ? "blitz" : "standard";
  return { scope, kinds: kinds.length ? kinds : DEFAULT_CONFIG.kinds, difficulty, rounds, timer, mode };
}

export function isValidGuess(g: any): g is { lat: number; lng: number } {
  return !!g && Number.isFinite(g.lat) && Number.isFinite(g.lng) && Math.abs(g.lat) <= 90 && Math.abs(g.lng) <= 540;
}

/** Normalises a longitude (MapLibre can report values past ±180 after panning around the globe). */
export function wrapLng(lng: number): number {
  return ((((lng + 180) % 360) + 360) % 360) - 180;
}

// ---------------------------------------------------------------- helpers

export function clamp(x: number, lo: number, hi: number): number { return Math.min(hi, Math.max(lo, x)); }

export function shuffle<T>(arr: T[], rand: () => number = Math.random): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(rand() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; }
  return a;
}

export const TYPE_LABEL: Record<PlaceType, string> = { cap: "capital", rcap: "state capital", city: "city", mark: "landmark" };
